import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight, Download, Loader2 } from 'lucide-react'

import { RefreshButton } from './RefreshButton'

import { api } from '@/api/client'
import { useFileDownload } from '@/lib/useFileDownload'
import { useToast } from '@/stores/toast'
import { useTabField } from '@/stores/tabState'
import { SearchBox } from './SearchBox'
import { MultiPartnerPicker } from './MultiPartnerPicker'
import { DateRangeBar } from './DateRangeBar'
import { cn, formatNum } from '@/lib/utils'

// 출고/입고 공통 라인 shape (SalesReportLine ≡ ReceivingLineRow)
interface AggregateLine {
  id: number
  order_id: number
  order_no: string
  order_date: string
  partner_code: string
  partner_name: string
  item_id: number
  item_code: string
  item_name: string
  unit: string
  qty: string
  unit_price: string
  supply_amount: string
  total: string
}
interface LinesResponse {
  lines: AggregateLine[]
  totals: { qty: string; supply_amount: string; vat_amount: string; total: string }
}
interface PartnerSummaryRow {
  partner_id: number
  partner_code: string
  partner_name: string
  qty_total: string
  supply_total: string
  vat_total: string
  total: string
}
interface PartnerSummaryResp { rows: PartnerSummaryRow[] }

export interface PartnerAggregateConfig {
  entityShort: string           // '출고' | '입고'
  entityLabel: string           // '거래처' | '매입처'
  entityNo: string              // '출고번호' | '입고번호'
  searchPlaceholder: string     // '품목·출고번호 검색' | '품목·입고번호 검색'
  partnerFilterPlaceholder: string  // '전체 거래처' | '전체 매입처'
  emptyDetailText: string       // '이 거래처의 출고 라인이 없습니다.' 등

  // API
  summaryApi: string            // '/api/delivery/partner-summary'
  summaryExportApi: string      // '/api/delivery/partner-summary/export'
  detailExportApi: string       // '/api/delivery/partner-detail/export' — 선택 거래처별 시트
  detailApi: string             // '/api/sales-report' | '/api/receiving/lines'
  detailAcceptsSort?: boolean   // sales-report만 sort/order 파라미터 받음
  queryKeyPrefix: string        // 'partner-summary' | 'rcv-partner-summary'
  excelFilePrefix: string       // '출고현황_거래처별' | '입고현황_매입처별'

  linkPath: '/main/delivery' | '/main/receiving'
  partnerBizClass?: 'customer' | 'vendor' | 'both' | ''
}

export function PartnerAggregateTab({ config: c }: { config: PartnerAggregateConfig }) {
  const push = useToast((s) => s.push)
  const { downloading, download } = useFileDownload()
  // 기간·검색·필터·선택 거래처는 탭을 오가도 유지(스코프 키=queryKeyPrefix → 출고/입고 분리).
  const k = c.queryKeyPrefix
  const [dateFrom, setDateFrom] = useTabField(k, 'dateFrom', '')   // 기본 빈 날짜(직접 지정)
  const [dateTo, setDateTo] = useTabField(k, 'dateTo', '')
  const [search, setSearch] = useTabField(k, 'search', '')
  const [groupSearch, setGroupSearch] = useTabField(k, 'groupSearch', '')   // 상위거래처(대분류) 검색
  const [filterPartners, setFilterPartners] = useTabField<number[]>(k, 'filterPartners', [])
  const [selectedPartner, setSelectedPartner] = useTabField<number | null>(k, 'selectedPartner', null)
  // 거래처별 시트 엑셀용 다중선택(체크박스). 세션 유지 불필요 → 로컬 state.
  const [checked, setChecked] = useState<Set<number>>(new Set())
  // Shift+클릭 범위선택 앵커(직전 체크한 행 인덱스, 표시 순서 기준).
  const [lastIdx, setLastIdx] = useState<number | null>(null)
  function toggleCheck(id: number, idx: number, shift: boolean) {
    if (shift && lastIdx !== null && lastIdx !== idx) {
      const [a, b] = idx < lastIdx ? [idx, lastIdx] : [lastIdx, idx]
      const rangeIds = partners.slice(a, b + 1).map((p) => p.partner_id)
      const check = !checked.has(id)   // 클릭한 행의 새 상태에 맞춰 범위 일괄 적용
      setChecked((cur) => {
        const next = new Set(cur)
        for (const rid of rangeIds) { if (check) next.add(rid); else next.delete(rid) }
        return next
      })
      setLastIdx(idx)
      return
    }
    setChecked((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setLastIdx(idx)
  }

  async function downloadExcel() {
    if (!partners.length) { push('내보낼 데이터가 없습니다', 'warn'); return }
    const p = new URLSearchParams({ from: dateFrom, to: dateTo })
    if (filterPartners.length) p.set('partner', filterPartners.join(','))
    if (search) p.set('q', search)
    if (groupSearch) p.set('group', groupSearch)
    await download(`${c.summaryExportApi}?${p}`, `${c.excelFilePrefix}_${dateFrom}_${dateTo}.xlsx`)
  }
  async function downloadDetailExcel() {
    const ids = [...checked]
    if (!ids.length) { push(`${c.entityLabel}를 체크하세요`, 'warn'); return }
    const p = new URLSearchParams({ from: dateFrom, to: dateTo, partner: ids.join(',') })
    if (search) p.set('q', search)
    if (groupSearch) p.set('group', groupSearch)
    await download(`${c.detailExportApi}?${p}`, `${c.excelFilePrefix}_상세_${dateFrom}_${dateTo}.xlsx`)
  }

  const { data: summary, refetch: refetchSummary, isFetching: fetchingSummary } = useQuery({
    queryKey: [c.queryKeyPrefix, dateFrom, dateTo, filterPartners, search, groupSearch],
    queryFn: () => {
      const p = new URLSearchParams({ from: dateFrom, to: dateTo })
      if (filterPartners.length) p.set('partner', filterPartners.join(','))
      if (search) p.set('q', search)
      if (groupSearch) p.set('group', groupSearch)
      return api<PartnerSummaryResp>(`${c.summaryApi}?${p}`)
    },
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })
  const partners = summary?.rows ?? []

  useEffect(() => {
    if (selectedPartner && partners.some((r) => r.partner_id === selectedPartner)) return
    setSelectedPartner(partners[0]?.partner_id ?? null)
  }, [partners, selectedPartner])

  const { data: detail, refetch: refetchDetail, isFetching: fetchingDetail } = useQuery({
    enabled: !!selectedPartner,
    queryKey: [`${c.queryKeyPrefix}-detail`, selectedPartner, dateFrom, dateTo],
    queryFn: () => {
      const p = new URLSearchParams({
        from: dateFrom, to: dateTo,
        partner: String(selectedPartner!),
      })
      if (c.detailAcceptsSort) { p.set('sort', 'date'); p.set('order', 'asc') }
      return api<LinesResponse>(`${c.detailApi}?${p}`)
    },
  })
  const lines = detail?.lines ?? []

  // 일자별 일계 삽입
  type Row = { kind: 'line'; line: AggregateLine } | { kind: 'subtotal'; date: string; qty: number; total: number }
  const rowsWithSubtotals: Row[] = []
  if (lines.length > 0) {
    let curDate = ''
    let acc = { qty: 0, total: 0 }
    for (const line of lines) {
      if (curDate && line.order_date !== curDate) {
        rowsWithSubtotals.push({ kind: 'subtotal', date: curDate, qty: acc.qty, total: acc.total })
        acc = { qty: 0, total: 0 }
      }
      curDate = line.order_date
      rowsWithSubtotals.push({ kind: 'line', line })
      acc.qty += Number(line.qty); acc.total += Number(line.total)
    }
    rowsWithSubtotals.push({ kind: 'subtotal', date: curDate, qty: acc.qty, total: acc.total })
  }

  const grandTotal = lines.reduce((s, l) => s + Number(l.total), 0)
  const selectedPartnerLabel = partners.find((p) => p.partner_id === selectedPartner)

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="mb-4 flex justify-end gap-2">
        <button onClick={downloadDetailExcel} disabled={downloading || !checked.size}
                title={`체크한 ${c.entityLabel}별로 시트를 나눠 상세내역 엑셀 생성`}
                className="bg-white hover:bg-emerald-50 disabled:opacity-50 border border-emerald-300 text-emerald-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          {c.entityLabel}별 상세 엑셀 {checked.size ? `(${checked.size})` : ''}
        </button>
        <button onClick={downloadExcel} disabled={downloading || !partners.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>
      <DateRangeBar from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}>
        <RefreshButton busy={fetchingSummary || fetchingDetail}
                       onClick={() => { void refetchSummary(); void refetchDetail() }} />
        <div className="flex-1 min-w-[180px]">
          <MultiPartnerPicker value={filterPartners} onChange={setFilterPartners}
                              bizClass={c.partnerBizClass ?? ''}
                              placeholder={c.partnerFilterPlaceholder}/>
        </div>
        <SearchBox value={groupSearch} onChange={setGroupSearch}
                   placeholder="상위거래처(대분류) 검색" className="flex-1 min-w-[160px]"/>
        <SearchBox value={search} onChange={setSearch}
                   placeholder={c.searchPlaceholder} className="flex-1 min-w-[160px]"/>
        <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">
          {c.entityLabel} {partners.length}건
        </span>
      </DateRangeBar>

      <div className="grid grid-cols-12 gap-4">
        {/* 좌측: 마스터 */}
        <div className="col-span-4 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide border-b">
            {c.entityLabel} 합계
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="px-2 py-2 text-center font-medium w-8">
                    <input type="checkbox"
                           checked={partners.length > 0 && partners.every((p) => checked.has(p.partner_id))}
                           onChange={(e) => setChecked(e.target.checked
                             ? new Set(partners.map((p) => p.partner_id)) : new Set())}
                           title="전체 선택 / 해제" className="accent-emerald-600 cursor-pointer"/>
                  </th>
                  <th className="px-2 py-2 text-left font-medium w-6"></th>
                  <th className="px-2 py-2 text-left font-medium w-20">코드</th>
                  <th className="px-2 py-2 text-left font-medium">{c.entityLabel}</th>
                  <th className="px-2 py-2 text-right font-medium w-28">합계금액</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p, i) => {
                  const active = p.partner_id === selectedPartner
                  return (
                    <tr key={p.partner_id}
                        onClick={() => setSelectedPartner(p.partner_id)}
                        className={cn('border-t border-slate-100 cursor-pointer',
                          active ? 'bg-emerald-50' : 'hover:bg-slate-50')}>
                      <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={checked.has(p.partner_id)} readOnly
                               onClick={(e) => toggleCheck(p.partner_id, i, e.shiftKey)}
                               title="Shift+클릭: 이전 체크부터 여기까지 한 번에"
                               className="accent-emerald-600 cursor-pointer"/>
                      </td>
                      <td className="px-2 py-1.5">
                        {active && <ChevronRight className="w-3.5 h-3.5 text-emerald-600"/>}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs tabular-nums text-slate-500">{p.partner_code}</td>
                      <td className={cn('px-2 py-1.5', active ? 'font-semibold text-emerald-900' : 'text-slate-700')}>
                        {p.partner_name}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatNum(p.total)}</td>
                    </tr>
                  )
                })}
                {!partners.length && (
                  <tr><td colSpan={5} className="text-center text-slate-400 py-8 text-sm">데이터 없음</td></tr>
                )}
              </tbody>
              {partners.length > 0 && (
                <tfoot className="bg-emerald-50/50 font-bold border-t-2 border-emerald-200">
                  <tr>
                    <td colSpan={4} className="px-2 py-2 text-right text-xs text-emerald-800 uppercase">합 계</td>
                    <td className="px-2 py-2 text-right tabular-nums text-emerald-900">
                      {formatNum(partners.reduce((s, r) => s + Number(r.total), 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* 우측: 라인 + 일계 */}
        <div className="col-span-8 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide border-b flex items-center justify-between">
            <span>{c.entityShort} 라인 {selectedPartnerLabel && `— ${selectedPartnerLabel.partner_name}`}</span>
            <span className="text-slate-400 normal-case tracking-normal">{lines.length}건</span>
          </div>
          <div className="overflow-y-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 text-xs text-slate-500 uppercase whitespace-nowrap sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left font-medium w-36">{c.entityNo}</th>
                  <th className="px-2 py-2 text-left font-medium w-20">일자</th>
                  <th className="px-2 py-2 text-left font-medium w-24">품목코드</th>
                  <th className="px-2 py-2 text-left font-medium">품명</th>
                  <th className="px-2 py-2 text-center font-medium w-12">단위</th>
                  <th className="px-2 py-2 text-right font-medium w-16">수량</th>
                  <th className="px-2 py-2 text-right font-medium w-20">단가</th>
                  <th className="px-2 py-2 text-right font-medium w-24">공급가액</th>
                  <th className="px-2 py-2 text-right font-medium w-24">합계</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithSubtotals.map((r, i) =>
                  r.kind === 'line' ? (
                    <tr key={`l-${r.line.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-mono text-xs tabular-nums">
                        <Link to={c.linkPath}
                              search={{ order: r.line.order_id, from: dateFrom, to: dateTo }}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                              title="전표별 탭에서 열기">
                          {r.line.order_no}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 tabular-nums text-slate-600">{r.line.order_date}</td>
                      <td className="px-2 py-1.5 font-mono text-xs tabular-nums text-slate-500">{r.line.item_code}</td>
                      <td className="px-2 py-1.5 text-slate-900">{r.line.item_name}</td>
                      <td className="px-2 py-1.5 text-center text-slate-500 text-xs">{r.line.unit}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(r.line.qty)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(r.line.unit_price)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(r.line.supply_amount)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatNum(r.line.total)}</td>
                    </tr>
                  ) : (
                    <tr key={`s-${i}-${r.date}`} className="bg-emerald-50/60 border-t border-emerald-100">
                      <td colSpan={5} className="px-2 py-1 text-right text-xs font-semibold text-emerald-700">
                        {r.date} 일계
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-900 font-semibold">{formatNum(r.qty)}</td>
                      <td></td>
                      <td></td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-900 font-semibold">{formatNum(r.total)}</td>
                    </tr>
                  )
                )}
                {!lines.length && (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-12 text-sm">
                    {selectedPartner ? c.emptyDetailText : `좌측에서 ${c.entityLabel}를 선택하세요.`}
                  </td></tr>
                )}
              </tbody>
              {lines.length > 0 && (
                <tfoot className="bg-emerald-100 font-bold border-t-2 border-emerald-300">
                  <tr>
                    <td colSpan={8} className="px-2 py-2 text-right text-xs text-emerald-900 uppercase">총 합계</td>
                    <td className="px-2 py-2 text-right tabular-nums text-emerald-900">{formatNum(grandTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
